import type { CustomerExpense } from '@clockwise/clients/customer-expenses';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { customerExpensesApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { formatCostOrVariable } from './expense-catalog-form';

interface Props {
  customerId: string;
  /** The invoice date. Only expenses in force on that date are offered — an
   *  expense that ended in May must not be billable on a July invoice. */
  asOf: string;
  disabled?: boolean;
  onPick: (expense: CustomerExpense) => void;
  'aria-label'?: string;
}

/** Enough to cover any real customer's standing charges; the endpoint pages,
 *  and a customer with more than this has a data problem, not a UI problem. */
const LIMIT = 100;

/**
 * Picks one of a customer's standing expenses to bill on this invoice.
 *
 * The chosen row's description and cost are COPIED onto the invoice line, not
 * referenced. Same copy-down rule as everywhere else in this domain: the
 * customer's expense record may be re-priced or ended next month, and an
 * invoice already raised must keep saying what it charged. The line does keep
 * `sourceType`/`sourceId` pointing back, which is what lets the month-end run
 * and any auditor answer "was this expense billed, and where".
 *
 * A VARIABLE expense is offered like any other, and copies down an empty
 * amount for the user to type. Invoice generation skips such a line precisely
 * because it has no amount to bill; this is the path that gives it one.
 *
 * Renders nothing selectable when the customer has no expenses in force —
 * an empty dropdown that looks clickable is worse than a disabled one that
 * says why.
 */
export function CustomerExpensePicker({
  customerId,
  asOf,
  disabled,
  onPick,
  'aria-label': ariaLabel,
}: Props) {
  const query = useQuery({
    queryKey: queryKeys.customerExpenses.list(customerId, { asOf, limit: LIMIT }),
    queryFn: () => customerExpensesApi.list(customerId, { asOf, limit: LIMIT }),
    enabled: Boolean(customerId && asOf),
  });

  const expenses = query.data?.data ?? [];

  /**
   * Controlled, and reset to empty after every pick.
   *
   * Left uncontrolled, Radix remembers its own selection and suppresses
   * `onValueChange` when the chosen item matches it — so re-picking the same
   * expense to undo a mistyped amount silently did nothing. Resetting means
   * every selection is a change and always fires.
   *
   * It also stops the trigger asserting a provenance the line may no longer
   * have: once the operator edits the description or amount by hand, a
   * trigger still reading 'Netsuite Subscription' would be a claim about the
   * line that isn't true. The real provenance lives on the line's
   * `sourceType`/`sourceId`, not in this control's label.
   */
  const [selected, setSelected] = useState('');

  if (query.isError) {
    // G15: a failed load must not look like "this customer has no expenses".
    return <p className="text-destructive text-xs">Could not load this customer's expenses.</p>;
  }

  const empty = !query.isLoading && expenses.length === 0;

  return (
    <Select
      value={selected}
      disabled={disabled || query.isLoading || empty}
      onValueChange={(id) => {
        const picked = expenses.find((e) => e.id === id);
        if (picked) onPick(picked);
        setSelected('');
      }}
    >
      <SelectTrigger aria-label={ariaLabel ?? 'Expense'}>
        <SelectValue
          placeholder={
            query.isLoading
              ? 'Loading expenses…'
              : empty
                ? 'No expenses in force on this date'
                : 'Pick an expense…'
          }
        />
      </SelectTrigger>
      <SelectContent>
        {expenses.map((e) => (
          <SelectItem key={e.id} value={e.id}>
            {e.description} — {formatCostOrVariable(e.cost)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
